if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          dots: this.querySelector('.slider-dots'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        
        if (this.elements.thumbnails) {
          this.elements.thumbnails.addEventListener('click', (e) => {
            const mediaToSwitch = e.target.closest('[data-target]');
            if (mediaToSwitch && mediaToSwitch.dataset.target) {
              e.preventDefault();
              this.setActiveMedia(mediaToSwitch.dataset.target, false);
            }
          });
          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }
        
        if (this.elements.dots) {
          this.elements.dots.addEventListener('click', (e) => {
            const dot = e.target.closest('.slider-dot');
            if (dot && dot.dataset.mediaId) {
              e.preventDefault();
              this.setActiveMedia(dot.dataset.mediaId, false);
            }
          });
        }

        this.updateThumbnailVisibility();
      }

      /**
       * Gets the currently selected variant option value from the product form (first option, e.g. Color).
       * Returns null if there is no variant picker (single variant product).
       */
      getSelectedVariantOptionValue() {
        const variantSelects = this.closest('product-info')?.querySelector('variant-selects');
        if (!variantSelects) return null;
        const select = variantSelects.querySelector('select');
        if (select?.selectedOptions?.length) return (select.selectedOptions[0].value || '').trim();
        const checked = variantSelects.querySelector('fieldset input:checked');
        return (checked?.value || '').trim();
      }

      /**
       * Show thumbnails whose data-alt-attribute is blank or matches the selected variant option value.
       * Hide thumbnails whose data-alt-attribute is not blank and does not match.
       * Comparison is case-insensitive. When there is no variant picker, all thumbnails are shown.
       */
      updateThumbnailVisibility() {
        if (!this.elements.thumbnails) return;
        const selectedRaw = this.getSelectedVariantOptionValue();
        if (selectedRaw === null) {
          this.elements.thumbnails.querySelectorAll('.thumbnail-list__item.slider__slide').forEach((el) => {
            el.style.display = '';
          });
          return;
        }
        const selected = (selectedRaw || '').trim().toLowerCase();
        const items = this.elements.thumbnails.querySelectorAll('.thumbnail-list__item.slider__slide');
        items.forEach((el) => {
          const alt = (el.dataset.altAttribute || '').trim().toLowerCase();
          const hide = alt !== '' && alt !== selected;
          el.style.display = hide ? 'none' : '';
        });
      }

      onSlideChanged(event) {
        const mediaId = event.detail.currentElement.dataset.mediaId;
        
        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${mediaId}"]`
          );
          this.setActiveThumbnail(thumbnail);
        }
        
        if (this.elements.dots) {
          this.setActiveDot(mediaId);
        }
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          if (activeMedia.parentElement && activeMedia.parentElement.firstChild !== activeMedia) {
            activeMedia.parentElement.prepend(activeMedia);
          }

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails || this.elements.dots) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(activeThumbnail);
          if (activeThumbnail) {
            this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
          }
        }
        
        if (this.elements.dots) {
          this.setActiveDot(mediaId);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      setActiveDot(mediaId) {
        if (!this.elements.dots) return;

        this.elements.dots
          .querySelectorAll('.slider-dot')
          .forEach((dot) => dot.classList.remove('slider-dot--active'));
        
        const activeDot = this.elements.dots.querySelector(`[data-media-id="${mediaId}"]`);
        if (activeDot) {
          activeDot.classList.add('slider-dot--active');
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
